<template>
  <AsidePanel
    ref="asidePanel"
    :cancelText="cancelText"
    :components="components"
    :handleMountedCallback="handleMountedCallback"
    :handleSave="isItemBeingUpdated ? handleSave : undefined"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :navigateToTab="activeTab"
    :redirectTo="devicesListRoute"
    :saveText="saveText"
    :showSaveButton="showSaveButton"
    :viewport="viewport"
    @cta-change="handleCTAChange"
  >
    <template v-slot:avatar>
      <AsideIcon
        :icon="avatarIcon"
      />
    </template>
    <template
      v-slot:leftPaneCustomContent
    >
      <DeviceStatus
        v-if="!isItemBeingCreated && !isAndroid"
      />
      <DeviceSupportCommands
        v-if="shouldShowDeviceSupportCommands"
      />
      <SystemCommands
        v-if="shouldShowSystemCommands"
        @navigateToMdmTab="handleNavigateToMdmTab"
      />
      <SeverityModal
        notificationTitle="This will alter who can log in to your devices."
        :primaryButtonProps="primaryButtonProps"
        :showModal="showModal"
        :title="userModalTitle"
        variant="sev2"
        @close="handleClose"
        @save="handleModalConfirm"
      >
        <template v-slot:subtitle>
          <span>
            <b>Are you sure you want to change this device's assigned users?</b>
          </span>
        </template>
        <template v-slot:body>
          <ParagraphBody
            :data-test-id="$testId('modalBody')"
          >
            {{ userModalBody }}
          </ParagraphBody>
        </template>
        <template v-slot:learnmore>
          <ParagraphBody>
            For more information, see
            <Link
              :href="KBArticle"
              :isExternal="false"
              target="_blank"
              text="Binding Users to Devices."
            />
          </ParagraphBody>
        </template>
      </SeverityModal>
    </template>
    <template
      v-if="isItemBeingCreated && !isAndroid"
      v-slot:instructions
    >
      <SystemInstructions />
    </template>
  </AsidePanel>
</template>
<script>
import {
  DeviceTabs,
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  Link,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import AndroidEmm from 'client/systems/AndroidEmm';
import AndroidPolicyResults from 'client/systems/AndroidPolicyResults';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelMethodMixin from 'client/components/AsidePanel/mixins/AsidePanelMethodMixin';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import CreateAndroidDevice from 'client/systems/CreateAndroidDevice';
import CreateChefSystem from 'client/systems/CreateChefSystem.vue';
import CreateIOSDevice from 'client/systems/CreateIOSDevice.vue';
import CreateLinuxSystem from 'client/systems/CreateLinuxSystem.vue';
import CreateMacSystem from 'client/systems/CreateMacSystem.vue';
import CreatePuppetSystem from 'client/systems/CreatePuppetSystem.vue';
import CreateWindowsSystem from 'client/systems/CreateWindowsSystem.vue';
import DeviceAppStatusItemTable from 'client/SoftwareApps/DeviceAppStatusItemTable.vue';
import DeviceHighlights from 'client/systems/DeviceHighlights/DeviceHighlights.vue';
import DeviceStatus from 'client/systems/DeviceStatus.vue';
import DeviceSupportCommands from 'client/systems/DeviceSupportCommands';
import DeviceWindowsMdm from 'client/systems/DeviceWindowsMdm';
import FeatureTrialMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PoliciesItemTable from 'client/components/ItemTable/PoliciesItemTable.vue';
import PolicyApi from 'client/api/v2/PolicyApi';
import RemoteAssistApi from 'client/api/v2/RemoteAssistApi';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemCommands from 'client/systems/SystemCommands';
import SystemDetailsWrap from 'client/systems/SystemDetailsWrap.vue';
import SystemGroupItemTable from 'client/components/ItemTable/SystemGroupItemTable.vue';
import SystemInstructions from 'client/systems/SystemInstructions.vue';
import SystemMdm from 'client/systems/SystemMdm.vue';
import SystemPoliciesIntro from 'client/systems/SystemPoliciesIntro.vue';
import SystemPolicyStatusItemTable from 'client/components/ItemTable/SystemPolicyStatusItemTable.vue';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';
import UserStateMixin from 'client/components/mixins/UserStateMixin';

const { create, update } = ItemActionConsts;
const { PrimaryButtonProps } = Types;
const {
  configuration,
  device,
  user,
} = ItemTypeConsts;
const { ConfigurationItemType, DeviceItemType } = ItemType;
const configurationItem = new ConfigurationItemType();
const policyCollection = 'PolicyCollection';
const systemPolicyStatusCollection = 'SystemPolicyStatusCollection';
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemModel = StoreModelMap[device];
const { parentOSType } = ItemTableConsts;
const {
  android,
  darwin,
  ios,
  linux,
  windows,
} = parentOSType;
const deviceItem = new DeviceItemType();

export default {
  name: 'SystemAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    DeviceStatus,
    DeviceSupportCommands,
    Link,
    ParagraphBody,
    SeverityModal,
    SystemInstructions,
    SystemCommands,
  },

  mixins: [
    AsidePanelMethodMixin,
    AsidePanelPropsMixin,
    FeatureTrialMixin,
    UserStateMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: device,
    },
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
    os: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      KBArticle: KbArticleUrls.bindingUsersToDevices,
      activeTab: this.navigateToTab,
      cancelText: '',
      components: [],
      devicesListRoute: ItemTypeRoutes[ItemTypeConsts.deviceList],
      itemHasBeenSaved: false,
      modalConfirmClicked: false,
      rasEnabled: true,
      saveText: '',
      showModal: false,
      showSaveButton: false,
      userModalActionLabel: '',
      userModalBody: '',
      userModalNotificationTitle: '',
      userModalSubtitle: '',
      userModalTitle: '',

    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.isProcessing()) {
      const processingData = this.getProcessingData();
      if (processingData) {
        const componentProps = {
          headerText: processingData.modalProps.headerText,
          mdTemplate: processingData.modalProps.mdTemplate,
          handleContinue: () => {
            ModalEventBus.close();
            next();
          },
          showClose: false,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
          preventCloseOnOverlayClick: true,
        });
        return;
      }
    }
    next();
  },

  computed: {
    ...mapGetters(policyCollection, {
      getPolicy: 'getItem',
    }),

    ...mapGetters(systemModel, [
      'isDesktopCapable',
      'isRemoteAssistLinuxSupportedDistro',
      'displayName',
      'isItemBeingCreated',
      'isItemBeingUpdated',
      'isIOS',
      'isLinux',
      'isMacOS',
      'isWindows',
      'isWindowsDomainJoined',
      'isAzureDomainJoined',
      'getTargetsToBind',
      'isAndroid',
    ]),
    ...mapGetters(settingsModule, [
      'hasRemoteAssistScope',
      'hasRemoteAssistSessionsScope',
      'hasWindowsMdmEnabled',
    ]),

    ...mapState('FeatureFlagsModule', {
      deviceAsideHighlightsTab: state => state.betaFeatures.deviceAsideHighlightsTab,
      remoteAssist: state => state.betaFeatures.remoteAssist,
      remoteAssistForLinux: state => state.betaFeatures.remoteAssistForLinux,
      windowsMdmEnrollment: state => state.betaFeatures.windowsMdmEnrollment,
    }),

    ...mapState(systemModel, ['previousItem']),

    ...mapState(settingsModule, [
      'connectKey',
      'kickstart',
    ]),

    avatarIcon() {
      if (this.isIOS) {
        return 'ios';
      } if (this.isLinux) {
        return 'linux';
      } if (this.isMacOS) {
        return 'apple';
      } if (this.isAzureDomainJoined || this.isWindowsDomainJoined || this.isWindows) {
        return 'windows';
      } if (this.isAndroid) {
        return 'android';
      }
      return 'devices';
    },

    groupLabel() {
      return `${deviceItem.title} Groups`;
    },

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        hasOutline: false,
        text: this.userModalActionLabel,
        type: 'primary',
      });
    },

    remoteAssistEnabled: {
      get() {
        this.fetchRemoteAssistConfig();
        return this.rasEnabled;
      },
      set(newValue) {
        this.rasEnabled = newValue;
      },
    },

    shouldShowDeviceSupportCommands() {
      return (this.isMacOS || this.isWindows
        || (this.isLinux && this.isDesktopCapable
        && this.$hasFeatureFlag(this.$featureFlags.remoteAssistForLinux)
        && this.isRemoteAssistLinuxSupportedDistro))
        && this.$hasFeatureFlag(this.$featureFlags.remoteAssist)
        && (this.hasRemoteAssistScope || this.hasRemoteAssistSessionsScope)
        && this.remoteAssistEnabled;
    },

    shouldShowSystemCommands() {
      const areWindowsCommandsEnabled = this.isWindows;
      const areLinuxCommandsEnabled = this.isLinux;
      const isValidAppleOs = this.isMacOS || this.isIOS;
      const isAndroidCommandsEnabled = this.isAndroid;

      return isValidAppleOs || areLinuxCommandsEnabled
            || areWindowsCommandsEnabled || isAndroidCommandsEnabled;
    },

    shouldShowWindowsMdmTab() {
      return this.isWindows
        && this.windowsMdmEnrollment
        && this.hasWindowsMdmEnabled;
    },

    systemUserItemTableProps() {
      const props = {
        boundItemsUrl: `/api/v2/systems/${this.itemId}/users`,
        columns: this.devicesSystemUserDefaultColumns,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.systemAsidePanel,
        showMfaStatus: this.previousItem.allowMultiFactorAuthentication,
      };
      return props;
    },
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    ...mapActions(systemModel, [
      'saveItem',
      'bindPolicyToSystem',
      'bindSystemToSystemGroups',
      'bindSystemToUsers',
      'bindSudoStatus',
    ]),

    ...mapGetters(systemModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'getUserAssociationChanges',
      'isProcessing',
      'getProcessingData',
    ]),

    ...mapActions(systemPolicyStatusCollection, ['fetchFilteredTotalCount', 'setSystemId']),

    async fetchRemoteAssistConfig() {
      try {
        const data = await RemoteAssistApi.getConfig();
        this.rasEnabled = data.enabled;
      } catch (err) {
        const message = get(err, 'response.data.message', 'Unable to fetch Remote Assist Settings');
        this.triggerErrorNotification({ title: 'Error', message });
      }
    },

    handleCTAChange(selectedIndex) {
      if (this.systemInsightsRefactor && this.deviceAsideHighlightsTab) {
        const detailsIndex = this.components.findIndex(({ label }) => label === 'Details');

        if (selectedIndex !== detailsIndex) {
          this.setTabProps('Details', {
            autoFocusSection: {},
          });
        }
      }
    },

    handleClose() {
      this.showModal = false;
    },

    handleInitialNavigateToTab() {
      const initialTabComp = this.components.find(comp => comp.tab === this.os);
      if (initialTabComp) {
        this.activeTab = { tabLabel: initialTabComp.label };
      }
    },

    handleModalConfirm() {
      this.modalConfirmClicked = true;
      this.showModal = false;
      this.$refs.asidePanel.handleItemSave();
    },

    async handleMountedCallback() {
      if (this.navigateToTab) {
        this.navigateToAsideTab(this.navigateToTab.tabLabel);
      }

      if (this.isItemBeingCreated) {
        const vueComponentProps = {
          connectKey: this.connectKey,
          kickstart: `${this.kickstart}/Kickstart`,
        };

        this.cancelText = 'close';

        this.components.push(
          {
            label: 'Windows',
            tab: DeviceTabs.windows,
            vueComponent: CreateWindowsSystem,
            vueComponentProps,
          },
          {
            label: 'Mac',
            tab: DeviceTabs.macos,
            vueComponent: CreateMacSystem,
            vueComponentProps,
          },
          {
            label: 'Linux',
            tab: DeviceTabs.linux,
            vueComponent: CreateLinuxSystem,
            vueComponentProps,
          },
          { label: 'Puppet', vueComponent: CreatePuppetSystem, vueComponentProps },
          { label: 'Chef', vueComponent: CreateChefSystem, vueComponentProps },
          { label: 'iOS', vueComponent: CreateIOSDevice },
        );

        if (this.$hasFeatureFlag(this.$featureFlags.androidEmmMvp)) {
          this.components.push(
            { label: 'Android', vueComponent: CreateAndroidDevice },
          );
        }

        this.handleInitialNavigateToTab();
      } else if (this.isItemBeingUpdated) {
        this.cancelText = 'cancel';

        if (this.deviceAsideHighlightsTab) {
          this.components.push({
            label: 'Highlights',
            vueComponent: DeviceHighlights,
            vueComponentProps: {
              itemId: this.itemId,
              navigateToAsideTab: (tabLabel, sectionName, filter) => {
                if (
                  this.systemInsightsRefactor
                  && tabLabel === 'Details'
                  && sectionName
                  && filter
                ) {
                  this.setTabProps('Details', {
                    autoFocusSection: {
                      sectionName,
                      filter,
                    },
                  });
                }
                this.activeTab = { tabLabel };
              },
            },
          });
        }

        const usersTabProps = { ...this.systemUserItemTableProps };
        usersTabProps.showNewSudoWorkflow = true;

        this.components.push(
          {
            label: 'Details',
            vueComponent: SystemDetailsWrap,
            vueComponentProps: {},
          },
          {
            itemType: ItemTypeConsts.deviceGroup,
            label: this.groupLabel,
            headerLabels: {
              [create]: `Select ${this.groupLabel} to add this ${deviceItem.label} to`,
              [update]: ` is a member of the selected ${this.groupLabel}`,
            },
            vueComponent: SystemGroupItemTable,
            vueComponentProps: {
              boundItemsUrl: `/api/v2/systems/${this.itemId}/memberof`,
              inDetailsAsidePanel: true,
              panelType: ItemTableConsts.panelType.systemAsidePanel,
            },
          },
        );

        // Users Tab
        this.components.push(
          {
            itemType: ItemTypeConsts.systemUser,
            label: 'Users',
            headerLabels: {
              [create]: `Select Users to create accounts on this ${deviceItem.label} for`,
              [update]: 'has accounts for the following users',
            },
            vueComponent: SystemUserItemTable,
            vueComponentProps: usersTabProps,
          },
        );

        // EMM Tab
        if (this.isAndroid) {
          this.components.push(
            {
              label: 'EMM',
              vueComponent: AndroidEmm,
            },
          );
        }

        // MDM Tab
        if (this.isMacOS || this.isIOS) {
          this.components.push(
            {
              label: 'MDM',
              vueComponent: SystemMdm,
            },
          );
        }

        if (this.shouldShowWindowsMdmTab) {
          this.components.push(
            {
              label: 'MDM',
              vueComponent: DeviceWindowsMdm,
            },
          );
        }

        this.saveText = `save ${deviceItem.label}`;
        this.showSaveButton = true;

        this.renderAppsTab();
        await this.renderPolicyTabs();

        // Policy Results Tab
        if (this.isAndroid) {
          this.components.push(
            {
              label: 'Policy Results',
              vueComponent: AndroidPolicyResults,
            },
          );
        }
      }
    },

    handleNavigateToMdmTab() {
      if (!(this.isWindows || this.isLinux)) {
        this.activeTab = { tabLabel: 'MDM' };
      }
    },

    handleSave() {
      this.itemHasBeenSaved = true;
      let saveReturnValue;
      if (!this.haveUserAssociationsChanged() || this.modalConfirmClicked) {
        return this.saveItem()
          .then(this.bindPolicyToSystem)
          .then(this.bindSystemToSystemGroups)
          .then(this.bindSystemToUsers)
          .then((response) => {
            saveReturnValue = this.parseBindSuccessMethod(response, user);
            return this.bindSudoStatus();
          })
          .then(() => saveReturnValue)
          .catch((err) => {
            throw err instanceof Error ? err : Error('Unknown error');
          })
          .finally(() => this.refreshTrialData(trialFeatures.osPatchManagement.featureCode));
      }
      this.setUserBindingModalText();
      this.showModal = true;
      this.refreshTrialData(trialFeatures.osPatchManagement.featureCode);
      return new SaveReturnValue({
        canContinue: false,
        noNotification: true,
      });
    },

    /**
     * @todo Consider moving some aspect of this method to the AsidePanel
     * component. Both the RadiusAsidePanel and UserAsidePanel have similar
     * functionality. However, the parent component _will_ need the ability to
     * supplement the base functionality. For example, in addition to checking
     * if the item has changed, the UserAsidePanel checks if the associations
     * or public keys have changed too.
     */
    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    haveUserAssociationsChanged() {
      const { binds, unbinds } = this.getUserAssociationChanges();
      return !!((binds.length > 0 || unbinds.length > 0));
    },

    navigateToAsideTab(tabLabel) {
      this.activeTab = { tabLabel };
    },

    osPoliciesCount(osParent) {
      return PolicyApi.getOsPoliciesCount(osParent);
    },

    renderAppsTab() {
      // Apps Tab
      if (this.isMacOS || this.isIOS || this.isAndroid) {
        this.components.push(
          {
            label: 'Apps',
            vueComponent: DeviceAppStatusItemTable,
            vueComponentProps: {
              systemId: this.itemId,
              inDetailsAsidePanel: true,
              panelType: ItemTableConsts.panelType.systemAsidePanel,
            },
          },
        );
      }
    },

    async renderPolicyTabs() {
      let osParent = '';

      if (this.isMacOS) osParent = darwin;
      else if (this.isIOS) osParent = ios;
      else if (this.isWindows) osParent = windows;
      else if (this.isAndroid) osParent = android;
      else osParent = linux;

      const { totalCount } = await this.osPoliciesCount(osParent);

      if (totalCount > 0) {
        this.components.push(
          {
            itemType: ItemTypeConsts.policy,
            label: 'Policies',
            headerLabels: {
              [create]: `The following ${configurationItem.labelPlural} will be applied to this device`,
              [update]: `has the following ${configurationItem.labelPlural} applied`,
            },
            vueComponent: PoliciesItemTable,
            vueComponentProps: {
              boundItemsUrl: `/api/v2/systems/${this.itemId}/policies`,
              disableBoundCount: true,
              inDetailsAsidePanel: true,
              panelType: ItemTableConsts.panelType.systemAsidePanel,
              parentOS: osParent,
            },
          },
        );
      } else {
        this.components.push(
          {
            itemType: configuration,
            label: `${configurationItem.titlePlural}`,
            vueComponent: SystemPoliciesIntro,
          },
        );
      }

      await this.renderSystemPolicyStatus();
    },

    async renderSystemPolicyStatus() {
      this.setSystemId(this.itemId);

      const totalCount = await this.fetchFilteredTotalCount();
      if (totalCount) {
        const headerLabel = `has results for the following ${configurationItem.labelPlural}`;

        this.components.push({
          itemType: ItemTypeConsts.systemPolicyStatus,
          label: `${configurationItem.title} Results`,
          headerLabels: {
            [create]: headerLabel,
            [update]: headerLabel,
          },
          vueComponent: SystemPolicyStatusItemTable,
          vueComponentProps: {
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.systemAsidePanel,
            systemId: this.itemId,
          },
        });
      }
    },

    setTabProps(tabLabel, props = {}) {
      const component = this.components.find(({ label }) => label === tabLabel);

      if (component) {
        component.vueComponentProps = props;
      }
    },

    setUserBindingModalText() {
      const { binds, unbinds } = this.getUserAssociationChanges();
      const bindBody = 'Adding a user to a device will create local accounts on the device.';
      const unbindBody = 'Removing a user from a device will cause those users to lose all access to this device.';
      if (binds.length > 0 && unbinds.length > 0) {
        this.userModalTitle = 'Change Assigned User';
        this.userModalBody = `${bindBody} ${unbindBody}`;
        this.userModalActionLabel = 'Confirm';
      } else if (binds.length > 0 && unbinds.length === 0) {
        this.userModalTitle = 'Bind User to Device';
        this.userModalBody = bindBody;
        this.userModalActionLabel = 'Bind User';
      } else {
        this.userModalTitle = 'Unbind User from Device';
        this.userModalBody = unbindBody;
        this.userModalActionLabel = 'Unbind User';
      }
    },
  },
};
</script>
